import type { ComponentType } from 'react'

import { sleep } from '@app/utils/sleep'

import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import type { MountCloseCallback, MountContextInterface, MountedComponentProps, PushFunctionOptions } from './types'

const MOUNT_SYMBOL = Symbol('MOUNT_SYMBOL')
const ASSERT_ATTEMPTS = 25
const ASSERT_INTERVAL = 200

export const getMount = () =>
  createThunk((_dispatch, _getState, context): MountContextInterface | null => {
    return context.lookup<MountContextInterface>(MOUNT_SYMBOL) ?? null
  })

// The mount point is briefly absent while the route tree re-suspends (login,
// navigation). Wait it out before giving up so callers don't crash on a gap.
export const assertMount = () =>
  createThunk(async (_dispatch, _getState, context): Promise<MountContextInterface> => {
    for (let attempt = 0; attempt < ASSERT_ATTEMPTS; attempt++) {
      const mount = context.lookup<MountContextInterface>(MOUNT_SYMBOL)
      if (mount) return mount
      await sleep(ASSERT_INTERVAL)
    }
    throw new Error(`Context: ${String(MOUNT_SYMBOL)} not found`)
  })

export function assertMountPush<P extends MountedComponentProps>(
  component: Promise<ComponentType<P>>,
  options: PushFunctionOptions & { props: Omit<P, 'mount'> }
): ThunkAction<Promise<MountCloseCallback>>
export function assertMountPush(
  component: Promise<ComponentType<MountedComponentProps>>,
  options?: PushFunctionOptions
): ThunkAction<Promise<MountCloseCallback>>
export function assertMountPush(component: Promise<ComponentType<any>>, options?: PushFunctionOptions): ThunkAction<Promise<MountCloseCallback>> {
  return createThunk(async dispatch => {
    const mount = await dispatch(assertMount())
    return mount.push(component, options)
  })
}

export const setMount = (mount: MountContextInterface) =>
  createThunk((_dispatch, _getState, context) => {
    const rc = context.getRootContext()
    rc.set(MOUNT_SYMBOL, mount)
  })

export const unsetMount = () =>
  createThunk((_dispatch, _getState, context) => {
    context.getRootContext().unset(MOUNT_SYMBOL)
  })
